import {Endpoint, Process} from '../../types-aspera';

/** One process row as the tables show it: the free-form `data` JSON read once, here, not in the template. */
export interface ProcessRow {
  id: string;
  kind: string;
  /** The badge: an import is a Download, an export an Upload. */
  direction: 'Download' | 'Upload';
  state: string;
  message?: string | null;
  startDate: string;
  endDate?: string | null;
  /** The file's path on the share, once the worker confirmed it. */
  remotePath: string | null;
  /** Where it came from (s3://...), for an export. */
  sourceUri: string | null;
  /** Where an import lands (s3://...), set when the import starts. */
  destinationUri: string | null;
  /** The transfer's first line: an import reads from the share, an export from the publish bucket. */
  source: string | null;
  /** The transfer's second line: an import lands in the supplier's bucket, an export goes to the share. */
  destination: string | null;
  percent: number | null;
  /** Set when the transfer left from a fixed egress address. */
  egressIp: string | null;
}

export function toProcessRow(process: Pick<Process, 'id' | 'kind' | 'state' | 'message' | 'data' | 'startDate' | 'endDate'>): ProcessRow {
  const data = parseData(process.data);
  const remotePath = stringOrNull(data['remotePath']);
  const sourceUri = stringOrNull(data['sourceUri']);
  const imported = process.kind === 'import';
  const destinationUri = imported ? stringOrNull(data['destination']) : null;
  return {
    id: process.id,
    kind: process.kind,
    direction: imported ? 'Download' : 'Upload',
    state: process.state,
    message: process.message,
    startDate: process.startDate,
    endDate: process.endDate,
    remotePath,
    sourceUri,
    destinationUri,
    source: imported ? remotePath : sourceUri,
    destination: imported ? destinationUri : remotePath,
    percent: typeof data['percent'] === 'number' ? data['percent'] : null,
    egressIp: stringOrNull(data['egressIp']),
  };
}

function parseData(data: unknown): Record<string, unknown> {
  // AWSJSON reaches the client as the JSON text of the data map (verified against the sannies API, 2026-10-19):
  // parsed once here. Anything malformed shows as an empty row rather than breaking the table.
  if (typeof data !== 'string' || !data) {
    return {};
  }
  try {
    const parsed = JSON.parse(data);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

/** An import lands at its drop-off plus the path below the endpoint's folder. */
function stringOrNull(value: unknown): string | null {
  return typeof value === 'string' && value ? value : null;
}
